import { useState, useEffect } from "react";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LangContext";
import axios from "axios";
import PdfPreviewModal from "@/components/PdfPreviewModal";
import { FileText, Loader2, User, Building, Edit3, X, Eye, Globe, Scale, Shield, Calendar, ChevronDown, Calculator } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Section toggle
function Section({ title, icon: Icon, defaultOpen = false, children, badge }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="rounded-lg border border-white/5 bg-white/[0.02] overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between px-4 py-3 hover:bg-white/[0.03] transition-colors"
        data-testid={`section-toggle-${title.replace(/\s/g, "-").toLowerCase()}`}
      >
        <span className="flex items-center gap-2 text-xs font-['Outfit'] text-white">
          {Icon && <Icon className="w-3.5 h-3.5 text-[#00F0FF]" />}
          {title}
          {badge && <span className="ml-1 px-2 py-0.5 rounded-full bg-[#00F0FF]/15 text-[9px] font-['JetBrains_Mono'] text-[#00F0FF]">{badge}</span>}
        </span>
        <ChevronDown className={`w-4 h-4 text-[#606070] transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && <div className="px-4 pb-4 pt-1 space-y-3">{children}</div>}
    </div>
  );
}

const inputCls = "w-full bg-black/50 border border-white/10 text-white focus:border-[#00F0FF] focus:ring-1 focus:ring-[#00F0FF] px-3 py-2 placeholder:text-white/30 text-sm transition-colors outline-none rounded-lg";

export default function ContractModal({ project, analysis, hourlyRate, onClose }) {
  useLang();
  const { user } = useAuth();

  const defaultClient = project?.assigned_freelancer_name
    ? (user?.role === "freelancer" ? user?.name || "Freelancer" : "Auftraggeber")
    : (user?.role === "client" ? user?.name || "Auftraggeber" : "Auftraggeber");
  const defaultFreelancer = project?.assigned_freelancer_name
    || (user?.role === "freelancer" ? user?.name || "Freelancer" : "Freelancer");

  const [countries, setCountries] = useState([]);
  const arAvgHours = (analysis?.visualElements || []).reduce(
    (s, e) => s + ((e.hours_min || 0) + (e.hours_max || 0)) / 2, 0,
  );
  const [form, setForm] = useState({
    freelancerName: defaultFreelancer,
    clientName: defaultClient,
    customTerms: "",
    clientAddress: "",
    clientCountry: "DE",
    clientVatId: "",
    freelancerAddress: "",
    freelancerCountry: "DE",
    freelancerVatId: "",
    paymentDueDays: 14,
    warrantyMonths: 12,
    liabilityCap: "",
    confidentialityYears: 3,
    venueCity: "",
    currency: "EUR",
    hourlyRate: parseFloat(hourlyRate) || 85,
    applicableLawCountry: "",  // empty = follow clientCountry default
    effortSource: "ar",        // "ar" | "manual"
    customTotalHours: "",
  });
  const [previewBody, setPreviewBody] = useState(null);

  useEffect(() => {
    axios.get(`${API}/contract/countries`)
      .then(res => setCountries(res.data || []))
      .catch(() => setCountries([]));
  }, []);

  const rate = parseFloat(form.hourlyRate) || 85;
  const vis = analysis?.visualElements || [];
  const avgHours =
    form.effortSource === "manual" && parseFloat(form.customTotalHours) > 0
      ? parseFloat(form.customTotalHours)
      : arAvgHours;
  const totalPrice = avgHours * rate;
  const milestones = analysis?.milestones || [];
  const totalWeeks = milestones.reduce((s, m) => s + (m.weeks || 0), 0);
  const risks = analysis?.risks || [];

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  // Determine jurisdictional pair badge text
  const cc = (form.clientCountry || "DE").toUpperCase();
  const fc = (form.freelancerCountry || "DE").toUpperCase();
  const lawCC = (form.applicableLawCountry || cc).toUpperCase();
  const isCross = cc !== fc;
  const EU_EEA = new Set(["DE","AT","BE","BG","HR","CY","CZ","DK","EE","FI","FR","GR","HU","IE","IT","LV","LT","LU","MT","NL","PL","PT","RO","SK","SI","ES","SE","IS","LI","NO"]);
  const ADQ = new Set(["CH","GB","JP","KR","NZ","CA","AR","IL","UY"]);
  const region = (c) => EU_EEA.has(c) ? "EU/EWR" : (ADQ.has(c) ? "Angemessenheit" : "Drittland");
  const lawCountryName = countries.find(c => c.code === lawCC)?.name_de || lawCC;
  const pairBadge = isCross ? `${cc} ↔ ${fc} · ${region(cc)} ↔ ${region(fc)}` : `${cc} · National (${region(cc)})`;

  const handleGenerate = () => {
    const body = {
      hourlyRate: rate,
      freelancerName: form.freelancerName.trim() || "Freelancer",
      clientName: form.clientName.trim() || "Auftraggeber",
      customTerms: form.customTerms || null,
      clientAddress: form.clientAddress || null,
      clientCountry: form.clientCountry,
      clientVatId: form.clientVatId || null,
      freelancerAddress: form.freelancerAddress || null,
      freelancerCountry: form.freelancerCountry,
      freelancerVatId: form.freelancerVatId || null,
      applicableLawCountry: form.applicableLawCountry || null,
      effortSource: form.effortSource || "ar",
      customTotalHours: form.effortSource === "manual" && form.customTotalHours
        ? parseFloat(form.customTotalHours)
        : null,
      paymentDueDays: parseInt(form.paymentDueDays) || 14,
      warrantyMonths: parseInt(form.warrantyMonths) || 12,
      liabilityCap: form.liabilityCap ? parseFloat(form.liabilityCap) : null,
      confidentialityYears: parseInt(form.confidentialityYears) || 3,
      venueCity: form.venueCity || null,
      currency: form.currency || "EUR",
    };
    setPreviewBody(body);
  };

  return (
    <div className="fixed inset-0 flex items-center justify-center p-4" style={{ zIndex: 99999 }} onClick={onClose}>
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" />
      <div onClick={e => e.stopPropagation()} className="relative glass-surface rounded-xl max-w-3xl w-full max-h-[92vh] overflow-y-auto animate-fade-in-up"
        data-testid="contract-modal" style={{ border: "1px solid rgba(0,240,255,0.2)" }}>

        {/* Header */}
        <div className="sticky top-0 z-10 px-6 py-4 border-b border-white/5 bg-[#0A0A14]/95 backdrop-blur-xl flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-[#00F0FF]/10">
              <FileText className="w-5 h-5 text-[#00F0FF]" />
            </div>
            <div>
              <h2 className="font-['Outfit'] text-lg font-semibold text-white">Smart-Vertragsgenerator</h2>
              <p className="text-[10px] text-[#606070] font-['JetBrains_Mono']">{project.projectTitle}</p>
            </div>
          </div>
          <button data-testid="contract-modal-close" onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-full bg-white/5 hover:bg-white/10 transition-colors">
            <X className="w-4 h-4 text-[#A0A0B0]" />
          </button>
        </div>

        {/* Summary Cards */}
        <div className="px-6 py-4 grid grid-cols-4 gap-3 border-b border-white/5 bg-[#08080E]">
          <div className="text-center px-3 py-3 rounded-xl bg-white/[0.03] border border-white/5">
            <span className="text-xl font-['JetBrains_Mono'] font-bold text-white">{vis.length}</span>
            <span className="block text-[10px] text-[#606070] mt-0.5">Komponenten</span>
          </div>
          <div className="text-center px-3 py-3 rounded-xl bg-white/[0.03] border border-white/5">
            <span className="text-xl font-['JetBrains_Mono'] font-bold text-white">{Math.round(avgHours)}h</span>
            <span className="block text-[10px] text-[#606070] mt-0.5">Aufwand · ≈ {Math.round(avgHours / 8)} T.</span>
          </div>
          <div className="text-center px-3 py-3 rounded-xl bg-white/[0.03] border border-white/5">
            <span className="text-xl font-['JetBrains_Mono'] font-bold text-white">{totalWeeks}W</span>
            <span className="block text-[10px] text-[#606070] mt-0.5">Laufzeit</span>
          </div>
          <div className="text-center px-3 py-3 rounded-xl bg-[#00F0FF]/[0.05] border border-[#00F0FF]/15">
            <span className="text-xl font-['JetBrains_Mono'] font-bold neon-text">{(totalPrice / 1000).toFixed(1)}k</span>
            <span className="block text-[10px] text-[#606070] mt-0.5">{form.currency} (netto)</span>
          </div>
        </div>

        {/* Jurisdiction badge */}
        <div className="px-6 py-3 border-b border-white/5 bg-gradient-to-r from-[#00F0FF]/[0.05] to-transparent flex items-center gap-2">
          <Scale className="w-4 h-4 text-[#00F0FF]" />
          <span className="text-[11px] text-[#A0A0B0]">Jurisdiktion:</span>
          <span data-testid="jurisdiction-badge" className="text-[11px] font-['JetBrains_Mono'] font-bold text-[#00F0FF]">{pairBadge}</span>
          {isCross && (
            <span className="ml-auto text-[10px] text-[#FFB800] font-['JetBrains_Mono']">
              Cross-Border-Klauseln aktiv
            </span>
          )}
        </div>

        {/* Form */}
        <div className="px-6 py-4 space-y-3">
          {/* Auftraggeber Section */}
          <Section title="Auftraggeber" icon={Building} defaultOpen badge={countries.find(c => c.code === cc)?.name_de}>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[10px] text-[#A0A0B0] mb-1 block">Firmenname *</label>
                <input data-testid="contract-client-input" type="text" value={form.clientName}
                  onChange={e => set("clientName", e.target.value)} placeholder="z.B. Muster GmbH" className={inputCls} />
              </div>
              <div>
                <label className="text-[10px] text-[#A0A0B0] mb-1 block">Land *</label>
                <select data-testid="contract-client-country" value={form.clientCountry} onChange={e => set("clientCountry", e.target.value)} className={inputCls}>
                  {countries.map(c => <option key={c.code} value={c.code}>{c.name_de}{c.is_eu_eea ? " (EU/EWR)" : ""}</option>)}
                </select>
              </div>
              <div className="col-span-2">
                <label className="text-[10px] text-[#A0A0B0] mb-1 block">Anschrift (optional)</label>
                <textarea rows={2} value={form.clientAddress} onChange={e => set("clientAddress", e.target.value)}
                  placeholder="Strasse Nr.&#10;PLZ Stadt" className={`${inputCls} resize-none`} />
              </div>
              <div>
                <label className="text-[10px] text-[#A0A0B0] mb-1 block">USt-ID / VAT (optional)</label>
                <input value={form.clientVatId} onChange={e => set("clientVatId", e.target.value)} placeholder="DE123456789" className={inputCls} />
              </div>
            </div>
          </Section>

          {/* Auftragnehmer */}
          <Section title="Auftragnehmer" icon={User} defaultOpen badge={countries.find(c => c.code === fc)?.name_de}>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[10px] text-[#A0A0B0] mb-1 block">Name *</label>
                <input data-testid="contract-freelancer-input" type="text" value={form.freelancerName}
                  onChange={e => set("freelancerName", e.target.value)} placeholder="z.B. Max Mustermann" className={inputCls} />
              </div>
              <div>
                <label className="text-[10px] text-[#A0A0B0] mb-1 block">Land *</label>
                <select data-testid="contract-freelancer-country" value={form.freelancerCountry} onChange={e => set("freelancerCountry", e.target.value)} className={inputCls}>
                  {countries.map(c => <option key={c.code} value={c.code}>{c.name_de}{c.is_eu_eea ? " (EU/EWR)" : ""}</option>)}
                </select>
              </div>
              <div className="col-span-2">
                <label className="text-[10px] text-[#A0A0B0] mb-1 block">Anschrift (optional)</label>
                <textarea rows={2} value={form.freelancerAddress} onChange={e => set("freelancerAddress", e.target.value)}
                  placeholder="Strasse Nr.&#10;PLZ Stadt" className={`${inputCls} resize-none`} />
              </div>
              <div>
                <label className="text-[10px] text-[#A0A0B0] mb-1 block">USt-ID / VAT (optional)</label>
                <input value={form.freelancerVatId} onChange={e => set("freelancerVatId", e.target.value)} placeholder="z.B. PT123456789" className={inputCls} />
              </div>
              <div>
                <label className="text-[10px] text-[#A0A0B0] mb-1 block">Währung</label>
                <select value={form.currency} onChange={e => set("currency", e.target.value)} className={inputCls}>
                  {["EUR","USD","CHF","GBP","SEK","DKK","NOK","PLN","CZK","HUF","RON","JPY","CAD","AUD"].map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
            </div>
          </Section>

          {/* Vergütung & Aufwand */}
          <Section title="Vergütung & Aufwand" icon={Calculator} defaultOpen badge={`${rate} ${form.currency}/h · ${Math.round(avgHours)}h`}>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[10px] text-[#A0A0B0] mb-1 block">Stundenansatz ({form.currency})</label>
                <input
                  data-testid="contract-hourly-rate"
                  type="number" min="1" step="5" value={form.hourlyRate}
                  onChange={e => set("hourlyRate", e.target.value)}
                  placeholder="85"
                  className={inputCls}
                />
              </div>
              <div>
                <label className="text-[10px] text-[#A0A0B0] mb-1 block">Berechnungsbasis</label>
                <div className="flex rounded-lg border border-white/10 overflow-hidden">
                  <button
                    type="button"
                    data-testid="effort-source-ar"
                    onClick={() => set("effortSource", "ar")}
                    className={`flex-1 text-xs py-2 transition-colors ${form.effortSource === "ar" ? "bg-[#00F0FF]/15 text-[#00F0FF]" : "text-[#A0A0B0] hover:bg-white/5"}`}
                  >
                    Project AR ({Math.round(arAvgHours)}h)
                  </button>
                  <button
                    type="button"
                    data-testid="effort-source-manual"
                    onClick={() => set("effortSource", "manual")}
                    className={`flex-1 text-xs py-2 border-l border-white/10 transition-colors ${form.effortSource === "manual" ? "bg-[#FFB800]/15 text-[#FFB800]" : "text-[#A0A0B0] hover:bg-white/5"}`}
                  >
                    Eigene Berechnung
                  </button>
                </div>
              </div>
              {form.effortSource === "manual" && (
                <div className="col-span-2">
                  <label className="text-[10px] text-[#A0A0B0] mb-1 block">
                    Eigener Gesamtaufwand (Stunden)
                  </label>
                  <input
                    data-testid="custom-total-hours"
                    type="number" min="1" step="1" value={form.customTotalHours}
                    onChange={e => set("customTotalHours", e.target.value)}
                    placeholder={`z.B. ${Math.round(arAvgHours) || 100}`}
                    className={inputCls}
                  />
                  <p className="text-[10px] text-[#606070] mt-1 font-['JetBrains_Mono']">
                    ≈ {form.customTotalHours ? Math.round(parseFloat(form.customTotalHours)/8) : 0} Personentage à 8 h
                  </p>
                </div>
              )}
              <div className="col-span-2 px-3 py-2 rounded-lg bg-[#00F0FF]/[0.04] border border-[#00F0FF]/15 flex items-center justify-between">
                <span className="text-[11px] text-[#A0A0B0]">Auftragswert (netto):</span>
                <span data-testid="contract-total-price" className="text-sm font-['JetBrains_Mono'] font-bold text-[#00F0FF]">
                  {totalPrice.toLocaleString("de-DE", { maximumFractionDigits: 0 })} {form.currency}
                </span>
              </div>
            </div>
          </Section>

          {/* Anwendbares Recht */}
          <Section title="Anwendbares Recht & Gerichtsstand" icon={Scale} badge={lawCountryName}>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <label className="text-[10px] text-[#A0A0B0] mb-1 block">
                  Anwendbares Recht (Jurisdiktion)
                </label>
                <select
                  data-testid="contract-applicable-law"
                  value={form.applicableLawCountry}
                  onChange={e => set("applicableLawCountry", e.target.value)}
                  className={inputCls}
                >
                  <option value="">Standard: Recht des Auftraggeber-Landes ({countries.find(c => c.code === cc)?.name_de || cc})</option>
                  {countries.map(c => (
                    <option key={c.code} value={c.code}>
                      Recht von {c.name_de}{c.is_eu_eea ? " (EU/EWR)" : ""}
                    </option>
                  ))}
                </select>
                <p className="text-[10px] text-[#606070] mt-1.5 leading-relaxed">
                  Per Art. 3 Rom-I-VO können die Parteien das anwendbare Recht ausdrücklich wählen.
                  Default: Recht des Auftraggeber-Landes. Klauseln werden automatisch angepasst.
                </p>
              </div>
              <div>
                <label className="text-[10px] text-[#A0A0B0] mb-1 block">Gerichtsstand-Stadt (optional)</label>
                <input value={form.venueCity} onChange={e => set("venueCity", e.target.value)} placeholder="z.B. Zürich" className={inputCls} />
              </div>
            </div>
          </Section>

          {/* Kommerzielle Bedingungen */}
          <Section title="Kommerzielle Bedingungen" icon={Calendar}>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[10px] text-[#A0A0B0] mb-1 block">Zahlungsziel (Tage)</label>
                <input type="number" min="1" max="120" value={form.paymentDueDays} onChange={e => set("paymentDueDays", e.target.value)} className={inputCls} />
              </div>
              <div>
                <label className="text-[10px] text-[#A0A0B0] mb-1 block">Gewährleistung (Monate)</label>
                <input type="number" min="0" max="60" value={form.warrantyMonths} onChange={e => set("warrantyMonths", e.target.value)} className={inputCls} />
              </div>
              <div>
                <label className="text-[10px] text-[#A0A0B0] mb-1 block">Haftungsobergrenze ({form.currency}, leer = Auftragswert)</label>
                <input type="number" min="0" step="500" value={form.liabilityCap} onChange={e => set("liabilityCap", e.target.value)}
                  placeholder={`Standard: ${totalPrice.toFixed(0)}`} className={inputCls} />
              </div>
              <div>
                <label className="text-[10px] text-[#A0A0B0] mb-1 block">Vertraulichkeit (Jahre nach Ende)</label>
                <input type="number" min="0" max="10" value={form.confidentialityYears} onChange={e => set("confidentialityYears", e.target.value)} className={inputCls} />
              </div>
            </div>
          </Section>

          {/* Was der Vertrag enthält */}
          <Section title="Was der Vertrag enthält" icon={Shield} badge={`${15} §§`}>
            <div className="grid grid-cols-3 gap-2">
              {[
                { name: "§1 Parteien", detail: "Adresse, Land, USt-ID" },
                { name: "§2 Projektgegenstand", detail: "Titel, Kategorie, Beschreibung" },
                { name: "§3 Leistungsumfang", detail: `${vis.length} Komponenten` },
                { name: "§4 Vergütung", detail: `${rate} ${form.currency}/h, Verzugszinsen` },
                { name: "§5 Meilensteine", detail: `${milestones.length} Phasen, Zahlungsplan` },
                { name: "§6 Abnahme", detail: "14-Tage-Frist, Gewährleistung" },
                { name: "§7 Change-Requests", detail: "Formerfordernis, Nachtrag" },
                { name: "§8 Vertraulichkeit", detail: `${form.confidentialityYears} Jahre nach Ende` },
                { name: "§9 Haftung", detail: "Begrenzte Haftung, Ausnahmen" },
                { name: "§10 Recht & Gerichtsstand", detail: `Recht von ${lawCountryName} · ${pairBadge}` },
                { name: "§11 Datenschutz", detail: "DSGVO / SCCs nach Land" },
                { name: "§12 Geistiges Eigentum", detail: "Rechteübertragung, IP" },
                { name: "§13 Höhere Gewalt", detail: "Force Majeure, Kündigung" },
                { name: "Anhang Risiken", detail: `${risks.length} identifiziert` },
                { name: "Unterschriften", detail: "Beide Parteien" },
              ].map((item, i) => (
                <div key={i} className="px-3 py-2 rounded-lg bg-white/[0.02] border border-white/5">
                  <span className="text-[10px] text-white font-medium block">{item.name}</span>
                  <span className="text-[9px] text-[#606070] font-['JetBrains_Mono']">{item.detail}</span>
                </div>
              ))}
            </div>
          </Section>

          {/* Custom terms */}
          <Section title="Zusätzliche Vereinbarungen (optional)" icon={Edit3}>
            <textarea data-testid="contract-terms-input" rows={3} value={form.customTerms}
              onChange={e => set("customTerms", e.target.value)}
              placeholder="z.B. spezielle NDA-Pflichten, Service-Level, Open-Source-Vorgaben..."
              className={`${inputCls} resize-none`} />
          </Section>
        </div>

        {/* Generate Button */}
        <div className="sticky bottom-0 px-6 py-4 border-t border-white/5 bg-[#0A0A14]/95 backdrop-blur-xl">
          <button
            data-testid="generate-contract-btn"
            onClick={handleGenerate}
            className="btn-primary w-full flex items-center justify-center gap-2 py-3.5 px-4 font-['Outfit'] font-semibold text-base rounded-lg"
          >
            <Eye className="w-5 h-5" /> Vertrag-Vorschau anzeigen
          </button>
          <p className="text-[10px] text-[#606070] font-['JetBrains_Mono'] text-center mt-2 flex items-center justify-center gap-1">
            <Globe className="w-3 h-3" /> Klauseln werden automatisch an Recht von <b className="text-[#00F0FF] mx-1">{lawCountryName}</b> und Länderpaar {pairBadge} angepasst.
          </p>
        </div>
      </div>

      {/* PDF Preview Modal */}
      {previewBody && (
        <PdfPreviewModal
          title="Werkvertrag"
          subtitle={`${project.projectTitle} · ${form.clientName} ↔ ${form.freelancerName}`}
          filename={`Vertrag_${project.projectTitle.replace(/ /g, "_")}.pdf`}
          url={`${API}/projects/${project.id}/contract`}
          method="post"
          postBody={previewBody}
          extraDownloads={[
            {
              label: "Word (.docx)",
              url: `${API}/projects/${project.id}/contract-docx`,
              method: "post",
              postBody: previewBody,
              filename: `Vertrag_${project.projectTitle.replace(/ /g, "_")}.docx`,
              mediaType: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
            },
          ]}
          onClose={() => setPreviewBody(null)}
        />
      )}
    </div>
  );
}
